import { useMemo } from 'react';
import { PortalDsBadge } from 'src/components/portal-design-system';
import { DataTable, type DataTableColumn } from 'src/components/shared';
import {
  formatSubscriptionLabel,
  getSubscriptionBadgeVariant,
  resolvePortalWorkspaceCredits,
} from '../utils/portal-users.mappers';
import type { PortalWorkspaceSummary } from '../types/users.types';
import { CreditsCell } from './credits-cell';
import { MemberStatusBreakdownCell } from './member-status-breakdown-cell';

interface TeamsTableProps {
  teams: PortalWorkspaceSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRowClick: (team: PortalWorkspaceSummary) => void;
}

export function TeamsTable({
  teams,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onRowClick,
}: TeamsTableProps) {
  const columns = useMemo<DataTableColumn<PortalWorkspaceSummary>[]>(() => [
    {
      id: 'name',
      header: 'Team',
      accessor: (team) => team.workspace_name,
      render: (team) => (
        <span className="font-medium text-[#0D0F1A]">
          {team.workspace_name}
        </span>
      ),
    },
    {
      id: 'members',
      header: 'Members',
      accessor: (team) => team.member_count,
      render: (team) => (
        <span className="font-mono text-[13px] text-[#0D0F1A]">
          {team.member_count}
        </span>
      ),
    },
    {
      id: 'credits',
      header: 'Credits',
      accessor: (team) => team.monthly_used,
      render: (team) => (
        <CreditsCell user={resolvePortalWorkspaceCredits(team)} />
      ),
    },
    {
      id: 'subscription',
      header: 'Subscription',
      accessor: (team) => team.tier,
      render: (team) => (
        <PortalDsBadge
          variant={getSubscriptionBadgeVariant(team.tier)}
          pill
        >
          {formatSubscriptionLabel(team.tier)}
        </PortalDsBadge>
      ),
    },
    {
      id: 'status',
      header: 'Member status',
      accessor: (team) => team.member_count,
      render: (team) => (
        <MemberStatusBreakdownCell workspace={team} />
      ),
    },
  ], []);

  return (
    <DataTable
      rows={teams}
      columns={columns}
      pageSize={pageSize}
      getRowKey={(team) => team.workspace_id}
      emptyMessage="No teams match your search or filters. Try adjusting the criteria above."
      serverPagination={{
        page,
        pageSize,
        totalCount,
        onPageChange,
      }}
      onRowClick={onRowClick}
    />
  );
}
